import { e2eTestId } from "@e2e/test-ids";
import styled from "@emotion/styled";
import { DurationInput } from "@ui/components/inputs/duration-input";
import { SmallTextInput } from "@ui/components/inputs/text-input";
import { colors } from "@ui/style/colors";
import { stack } from "@ui/style/layout";
import { isNil, isNotNil } from "@utils/assert";
import React, { useCallback, useImperativeHandle, useMemo, useRef, useState } from "react";

export const InputWithArrows: React.FC<{
	value: number;
	onValueChange: React.Dispatch<React.SetStateAction<number>>;
	onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
	onError?: (error: boolean) => void;
	onBlur?: () => void;
	onFocus?: () => void;
	type?: "number" | "time";
	withArrows: boolean;
}> = ({ type = "number", value, onValueChange, onKeyDown, onError, onBlur, onFocus, withArrows }) => {
	// Because we don't want to send NaN to onValueChange but we sometimes want to display
	// nothing in the input (e.g: if the user erase everything), the value can be "masked"
	const [isValueMasked, setValueMasked] = useState<boolean>(false);
	const [error, setError] = useState(false);

	const incrementArrowsRef = useRef<IncrementArrowsRef>(null);

	const updateError = useCallback(
		(localError: boolean) => {
			setError(localError);
			onError?.(localError);
		},
		[onError],
	);

	const handleKeyDown = useCallback(
		(event: React.KeyboardEvent<HTMLInputElement>) => {
			switch (event.key) {
				case "Tab":
				case "Enter":
				case "Escape":
					updateError(false);
					break;
				case "ArrowUp":
				case "ArrowDown":
					incrementArrowsRef.current?.hightlightArrow(event.key === "ArrowUp" ? "up" : "down");
					break;
				case "Backspace":
					if (value.toString().length < 2) {
						setValueMasked(true);
					}
					break;
			}
			onKeyDown?.(event);
		},
		[onKeyDown, updateError, value],
	);

	const inputProps = useMemo(
		() =>
			type === "number"
				? { min: MIN_NUMBER, max: MAX_NUMBER, step: STEP_NUMBER }
				: { min: MIN_TIME, max: MAX_TIME, step: STEP_TIME },
		[type],
	);

	const updateValueAndError = useCallback(
		(localValue: number | undefined) => {
			if (numberIsValid(localValue, inputProps.min, inputProps.max)) {
				onValueChange(localValue);
				updateError(false);
				setValueMasked(false);
			} else {
				updateError(true);
			}
		},
		[inputProps.max, inputProps.min, onValueChange, updateError],
	);

	if (type === "number") {
		return (
			<>
				<SmallTextInput
					type="number"
					pattern="[0-9]*"
					inputMode="numeric"
					style={{ alignSelf: "center" }}
					{...inputProps}
					value={isValueMasked ? NaN : value}
					autoFocus
					onKeyDown={handleKeyDown}
					onBlur={() => {
						updateError(false);
						onBlur?.();
					}}
					onKeyUp={() => incrementArrowsRef.current?.stopHightlighting()}
					onFocus={onFocus}
					onChange={e => {
						const rep = parseInt(e.target.value);
						updateValueAndError(rep);
					}}
				/>
				{withArrows && (
					<IncrementArrows ref={incrementArrowsRef} {...inputProps} errorMode={error} onValueChange={onValueChange} />
				)}
			</>
		);
	}

	return (
		<>
			<DurationInput
				style={{ color: error ? colors.rejection : undefined }}
				{...inputProps}
				durationInSec={isValueMasked ? NaN : value}
				onKeyDown={handleKeyDown}
				autoFocus
				onDurationChange={updateValueAndError}
				onKeyUp={() => incrementArrowsRef.current?.stopHightlighting()}
				onFocus={onFocus}
				onBlur={() => {
					updateError(false);
					onBlur?.();
				}}
			/>
			{withArrows && (
				<IncrementArrows ref={incrementArrowsRef} {...inputProps} errorMode={error} onValueChange={onValueChange} />
			)}
		</>
	);
};

interface IncrementArrowsRef {
	hightlightArrow: (arrow: "up" | "down") => void;
	stopHightlighting: () => void;
}

const IncrementArrows = React.forwardRef<
	IncrementArrowsRef,
	{
		errorMode?: boolean;
		step: number;
		min: number;
		max: number;
		onValueChange: React.Dispatch<React.SetStateAction<number>>;
	}
>(({ errorMode, step, min, max, onValueChange }, ref) => {
	const [hightlightedArrow, setHightlightedArrow] = useState<null | "up" | "down">(null);
	const mouseIsDown = useRef(false);
	const nextIncrementTimeout = useRef<NodeJS.Timeout | null>(null);

	const incrementValue = useCallback(
		(localStep: number, timeout?: number) => {
			onValueChange(previousValue => {
				const nextValue = previousValue + localStep;
				if (nextValue >= min && nextValue <= max) {
					return nextValue;
				}
				return previousValue;
			});
			if (isNotNil(timeout)) {
				nextIncrementTimeout.current = setTimeout(() => incrementValue(localStep, timeout), timeout);
			}
		},
		[max, min, onValueChange],
	);

	const startIncrementation = useCallback(
		(localStep: number) => {
			mouseIsDown.current = true;
			incrementValue(localStep);
			nextIncrementTimeout.current = setTimeout(() => incrementValue(localStep, 30), 500);
		},
		[incrementValue],
	);

	const stopIncrementAndHighlight = useCallback(() => {
		mouseIsDown.current = false;
		if (isNotNil(nextIncrementTimeout.current)) {
			clearTimeout(nextIncrementTimeout.current);
		}
		setHightlightedArrow(null);
	}, []);

	useImperativeHandle(ref, () => ({
		hightlightArrow: (arrow: "up" | "down") => setHightlightedArrow(arrow),
		stopHightlighting: () => setHightlightedArrow(null),
	}));

	return (
		<ArrowContainer>
			<Arrow
				data-test={e2eTestId.createTemplatePage.arrowUp}
				style={!errorMode && hightlightedArrow === "up" ? { backgroundColor: colors.primaryDark } : undefined}
				errorMode={errorMode}
				onMouseDown={() => {
					setHightlightedArrow("up");
					startIncrementation(step);
				}}
				onMouseUp={stopIncrementAndHighlight}
			>
				<i className="pi pi-chevron-up" />
			</Arrow>
			<Arrow
				data-test={e2eTestId.createTemplatePage.arrowDown}
				style={!errorMode && hightlightedArrow === "down" ? { backgroundColor: colors.primaryDark } : undefined}
				errorMode={errorMode}
				onMouseDown={() => {
					setHightlightedArrow("down");
					startIncrementation(-step);
				}}
				onMouseUp={stopIncrementAndHighlight}
			>
				<i className="pi pi-chevron-down" />
			</Arrow>
		</ArrowContainer>
	);
});
IncrementArrows.displayName = "IncrementArrows";

const MIN_NUMBER = 1;
const MAX_NUMBER = 99;
const STEP_NUMBER = 1;
const MIN_TIME = 10;
const MAX_TIME = 9 * 60 + 50;
const STEP_TIME = 10;

function numberIsValid(value: number | undefined, min: number, max: number): value is number {
	if (isNil(value) || isNaN(value)) {
		return false;
	}
	return value >= min && value <= max;
}

const ArrowContainer = styled.div`
	position: absolute;
	height: 100%;
	top: 0;
	right: 0;
	display: flex;
	flex-direction: column;
`;

const Arrow = styled.div<{ errorMode?: boolean }>`
	${stack(undefined, "center", "center")}
	width: 20px;
	flex: 1;
	background-color: ${props => (props.errorMode ? colors.rejection : colors.primary)};
	color: ${colors.white};
	cursor: pointer;

	:hover {
		background-color: ${props => (props.errorMode ? colors.rejectionDark : colors.primaryDark50)};
	}

	i {
		margin-right: 2px;
		font-size: 10px;
	}
`;
